import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { localDateTimeToISO } from 'dashboard/helper/appointmentTime';

export function useAppointmentBooking({ conversationId, pipelineCardId } = {}) {
  const store = useStore();
  let accountGeneration = 0;
  let optionsRequestId = 0;
  let submitRequestId = 0;

  const currentAccountId = computed(() => {
    const getter = store.getters.getCurrentAccountId;
    return typeof getter === 'function' ? getter() : getter;
  });

  const normalizeAccountId = value => {
    if (value === undefined || value === null) return null;
    const normalized = String(value).trim();
    return normalized || null;
  };

  const captureAccountContext = () => ({
    accountId: normalizeAccountId(currentAccountId.value),
    generation: accountGeneration,
  });

  const isCurrentAccountContext = context =>
    Boolean(context?.accountId) &&
    context.accountId === normalizeAccountId(currentAccountId.value) &&
    context.generation === accountGeneration;

  const conversation = computed(() => {
    const getter = store.getters.getConversationById;
    if (typeof getter === 'function') return getter(conversationId) ?? null;
    return null;
  });

  const contact = computed(() => {
    const senderId =
      conversation.value?.meta?.sender?.id ??
      conversation.value?.contact_id ??
      null;
    if (!senderId) return null;
    const fromGetter = store.getters['contacts/getContact']?.(senderId);
    if (fromGetter && fromGetter.id) return fromGetter;
    const fromById = store.getters['contacts/getContactById']?.(senderId);
    if (fromById && fromById.id) return fromById;
    return null;
  });

  const professionalId = ref(null);
  const serviceId = ref(null);
  const datetime = ref('');
  const notes = ref('');
  const partnerId = ref(null);
  const loading = ref(false);
  const errors = ref(null);
  const successAppointment = ref(null);

  const professionals = computed(
    () => store.getters['professionals/getProfessionals'] ?? []
  );

  const allServices = computed(
    () => store.getters['appointmentServices/getAppointmentServices'] ?? []
  );

  // Filter services to those offered by the selected professional.
  // Falls back to showing all services when no professional is selected
  // or when the professional has no service_ids (e.g. old data without associations).
  const services = computed(() => {
    if (!professionalId.value) return allServices.value;
    const prof = professionals.value.find(
      p => p.id === Number(professionalId.value)
    );
    if (!prof || !prof.service_ids || prof.service_ids.length === 0) {
      return allServices.value;
    }
    return allServices.value.filter(s => prof.service_ids.includes(s.id));
  });

  // Reset service selection when professional changes to avoid stale value
  watch(professionalId, () => {
    serviceId.value = null;
  });

  const partners = computed(() => store.getters['partners/getPartners'] ?? []);

  async function fetchOptions() {
    const context = captureAccountContext();
    if (!isCurrentAccountContext(context)) return [];

    optionsRequestId += 1;
    const requestId = optionsRequestId;
    loading.value = true;
    try {
      return await Promise.all([
        store.dispatch('professionals/get', context).catch(() => null),
        store.dispatch('appointmentServices/get', context).catch(() => null),
        store.dispatch('partners/get').catch(() => null),
      ]);
    } finally {
      if (requestId === optionsRequestId && isCurrentAccountContext(context)) {
        loading.value = false;
      }
    }
  }

  async function submit() {
    const context = captureAccountContext();
    if (!isCurrentAccountContext(context)) return undefined;

    submitRequestId += 1;
    const requestId = submitRequestId;
    loading.value = true;
    errors.value = null;
    try {
      const payload = {
        contact_id: contact.value.id,
        professional_id: professionalId.value,
        service_id: serviceId.value,
        scheduled_at: localDateTimeToISO(datetime.value),
        notes: notes.value || undefined,
      };
      if (partnerId.value) payload.partner_id = partnerId.value;
      const displayId =
        conversation.value?.display_id ?? conversation.value?.id;
      if (displayId) payload.conversation_display_id = displayId;
      if (pipelineCardId) payload.pipeline_card_id = pipelineCardId;

      const appt = await store.dispatch('appointmentsModule/create', {
        ...payload,
        ...context,
      });
      if (!appt || !isCurrentAccountContext(context)) return undefined;

      successAppointment.value = appt;
      return appt;
    } catch (err) {
      if (!isCurrentAccountContext(context)) return undefined;

      errors.value = err?.response?.data?.errors ?? {
        base: [String(err?.message ?? 'unknown_error')],
      };
      throw err;
    } finally {
      if (requestId === submitRequestId && isCurrentAccountContext(context)) {
        loading.value = false;
      }
    }
  }

  function reset() {
    professionalId.value = null;
    serviceId.value = null;
    datetime.value = '';
    notes.value = '';
    partnerId.value = null;
    errors.value = null;
    successAppointment.value = null;
  }

  watch(currentAccountId, () => {
    accountGeneration += 1;
    optionsRequestId += 1;
    submitRequestId += 1;
    loading.value = false;
    reset();
  });

  return {
    conversation,
    contact,
    professionalId,
    serviceId,
    datetime,
    notes,
    partnerId,
    professionals,
    services,
    partners,
    loading,
    errors,
    successAppointment,
    fetchOptions,
    submit,
    reset,
  };
}
